
export default function Problem() {
  return (
    <section id="problem-section" className="py-16 sm:py-24 bg-white relative overflow-hidden">
      {/* Decorative dots */}
      <div className="absolute inset-0 dots-pattern"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-16 space-y-3 sm:space-y-4">
          <div className="inline-block glass px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-base sm:text-lg font-bold border border-red-400/30">
            <span className="text-red-400">El Problema Real</span>
          </div>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>
            Gestión de camas y altas: siempre un <span className="text-red-500 text-glow">quebradero de cabeza</span>
          </h2>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            Cada mañana, el mismo caos: llamadas, listas en papel, información dispersa y camas "vacías pero no disponibles"
          </p>
        </div>

        {/* Storyboard Animado */}
        <div className="mb-10 sm:mb-16 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 border border-red-200 shadow-lg">
          <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">Un día típico sin PreBedAI</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
            {/* Escena 1 */}
            <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-6 shadow-sm hover:scale-105 transition-all duration-300 border border-gray-200">
              <div className="w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-4 shadow-glow">
                <i className="ri-phone-line text-white text-xl sm:text-3xl"></i>
              </div>
              <p className="text-center text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">08:30 - Reunión de camas</p>
              <p className="text-center text-xs text-gray-600 hidden sm:block">15 llamadas para localizar camas disponibles</p>
              <p className="text-center text-xs text-gray-600 sm:hidden">15 llamadas</p>
            </div>

            {/* Escena 2 */}
            <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-6 shadow-sm hover:scale-105 transition-all duration-300 border border-gray-200">
              <div className="w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-500 to-orange-700 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-4 shadow-lg">
                <i className="ri-file-list-line text-white text-xl sm:text-3xl"></i>
              </div>
              <p className="text-center text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">09:15 - Listas dispersas</p>
              <p className="text-center text-xs text-gray-600 hidden sm:block">Excel, post-its y pizarras con información contradictoria</p>
              <p className="text-center text-xs text-gray-600 sm:hidden">Info contradictoria</p>
            </div>

            {/* Escena 3 */}
            <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-6 shadow-sm hover:scale-105 transition-all duration-300 border border-gray-200">
              <div className="w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-br from-amber-500 to-amber-700 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-4 shadow-lg">
                <i className="ri-question-line text-white text-xl sm:text-3xl"></i>
              </div>
              <p className="text-center text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">10:00 - Camas fantasma</p>
              <p className="text-center text-xs text-gray-600 hidden sm:block">“Vacía” en el sistema pero pendiente de limpieza</p>
              <p className="text-center text-xs text-gray-600 sm:hidden">Pendiente limpieza</p>
            </div>

            {/* Escena 4 */}
            <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-6 shadow-sm hover:scale-105 transition-all duration-300 border border-gray-200">
              <div className="w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-4 shadow-glow">
                <i className="ri-time-line text-white text-xl sm:text-3xl"></i>
              </div>
              <p className="text-center text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">14:00 - Demoras</p>
              <p className="text-center text-xs text-gray-600 hidden sm:block">Paciente esperando 4 horas por falta de coordinación</p>
              <p className="text-center text-xs text-gray-600 sm:hidden">4h de espera</p>
            </div>
          </div>

          {/* Ilustración del caos */}
          <div className="mt-6 sm:mt-8 rounded-lg sm:rounded-xl overflow-hidden border border-white/10">
            <img
              src="/images/problem-chaotic.jpg"
              alt="Caos en la coordinación hospitalaria"
              className="w-full h-40 sm:h-64 object-cover object-center"
            />
          </div>
        </div>

        {/* Datos concretos con glassmorphism */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 mb-8 sm:mb-12">
          {/* Dato 1 */}
          <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-red-200 hover:shadow-lg hover:scale-105 transition-all duration-300">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-xl flex items-center justify-center mb-3 sm:mb-4 shadow-glow">
              <i className="ri-alarm-warning-line text-white text-2xl sm:text-3xl"></i>
            </div>
            <p className="text-3xl sm:text-4xl font-black text-red-500 mb-1 sm:mb-2">15%</p>
            <p className="text-gray-900 font-bold mb-1 sm:mb-2 text-sm sm:text-base">Camas liberadas antes de las 12h</p>
            <p className="text-xs sm:text-sm text-gray-600">El 85% restante genera cuellos de botella para nuevos ingresos</p>
          </div>

          {/* Dato 2 */}
          <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-orange-200 hover:shadow-lg hover:scale-105 transition-all duration-300">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-500 to-orange-700 rounded-xl flex items-center justify-center mb-3 sm:mb-4 shadow-lg">
              <i className="ri-calendar-line text-white text-2xl sm:text-3xl"></i>
            </div>
            <p className="text-3xl sm:text-4xl font-black text-orange-500 mb-1 sm:mb-2">25%</p>
            <p className="text-gray-900 font-bold mb-1 sm:mb-2 text-sm sm:text-base">Pre-altas identificadas en 24h</p>
            <p className="text-xs sm:text-sm text-gray-600">Sin herramientas predictivas, la planificación es reactiva</p>
          </div>

          {/* Dato 3 */}
          <div className="glass rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-amber-400/30 hover:shadow-glow-pink hover:scale-105 transition-all duration-300">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-amber-500 to-amber-700 rounded-xl flex items-center justify-center mb-3 sm:mb-4 shadow-lg">
              <i className="ri-time-line text-white text-2xl sm:text-3xl"></i>
            </div>
            <p className="text-3xl sm:text-4xl font-bold text-amber-600 mb-1 sm:mb-2">14-18h</p>
            <p className="text-gray-900 font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Para volver a ocupar una cama</p>
            <p className="text-xs sm:text-sm text-gray-600">Tiempo medio desde alta hasta nuevo ingreso</p>
          </div>
        </div>

        {/* Coste oculto visual */}
        <div className="bg-gradient-to-br from-[#0A1628] to-[#1A2332] rounded-xl sm:rounded-2xl p-6 sm:p-12 text-white">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Izquierda - Texto */}
            <div className="space-y-4 sm:space-y-6">
              <h3 className="text-xl sm:text-3xl font-bold">El coste oculto de la coordinación manual</h3>
              <p className="text-[#B4BCD0] text-sm sm:text-lg">
                Más allá del tiempo perdido, cada día sin coordinación digital genera costes operativos evitables:
              </p>
              
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <p className="font-bold text-sm sm:text-lg">4 horas diarias de personal</p>
                    <p className="text-[#B4BCD0] text-xs sm:text-sm">En llamadas, búsquedas y coordinación manual</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-restaurant-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <p className="font-bold text-sm sm:text-lg">Dietas y lavandería innecesarias</p>
                    <p className="text-[#B4BCD0] text-xs sm:text-sm">Por demoras en altas y asignaciones</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-hospital-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <p className="font-bold text-sm sm:text-lg">Camas infrautilizadas</p>
                    <p className="text-[#B4BCD0] text-xs sm:text-sm">Vacías pero no disponibles por falta de visibilidad</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 sm:pt-6 border-t border-white/20">
                <p className="text-2xl sm:text-3xl font-bold text-[#06B6D4]">+200.000€ anuales</p>
                <p className="text-[#B4BCD0] text-sm">Coste estimado en un hospital de 300 camas</p>
              </div>
            </div>

            {/* Derecha - Gráfico circular */}
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-white/20">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0 sm:mb-6">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-500 rounded-xl flex items-center justify-center">
                      <i className="ri-time-line text-white text-2xl sm:text-3xl"></i>
                    </div>
                    <div>
                      <p className="text-2xl sm:text-4xl font-bold text-white">14-18h</p>
                      <p className="text-red-200 text-xs sm:text-sm">Tiempo medio de reocupación</p>
                    </div>
                  </div>
                  <div className="text-center sm:text-right">
                    <p className="text-xl sm:text-2xl font-bold text-white">-30%</p>
                    <p className="text-red-200 text-xs sm:text-sm">Eficiencia perdida</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de problemas */}
        <div className="mt-10 sm:mt-16 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6">
          <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-red-50 rounded-lg sm:rounded-xl border-l-4 border-red-500">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-close-line text-white text-sm sm:text-base"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">Llamadas constantes e interrupciones</p>
              <p className="text-xs sm:text-sm text-gray-600">Admisión llama a enfermería, enfermería a limpieza, limpieza no responde...</p>
            </div>
          </div>

          <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-red-50 rounded-lg sm:rounded-xl border-l-4 border-red-500">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-close-line text-white text-sm sm:text-base"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">Información dispersa y contradictoria</p>
              <p className="text-xs sm:text-sm text-gray-600">Excel, pizarras, post-its... cada equipo tiene su propia "verdad"</p>
            </div>
          </div>

          <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-red-50 rounded-lg sm:rounded-xl border-l-4 border-red-500">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-close-line text-white text-sm sm:text-base"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">Camas "vacías pero no disponibles"</p>
              <p className="text-xs sm:text-sm text-gray-600">Aparecen libres en el sistema pero nadie sabe si están limpias o en mantenimiento</p>
            </div>
          </div>

          <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-red-50 rounded-lg sm:rounded-xl border-l-4 border-red-500">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-close-line text-white text-sm sm:text-base"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">Planificación reactiva, no proactiva</p>
              <p className="text-xs sm:text-sm text-gray-600">Sin predicción de altas, todo se decide sobre la marcha</p>
            </div>
          </div>

          <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-red-50 rounded-lg sm:rounded-xl border-l-4 border-red-500">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-close-line text-white text-sm sm:text-base"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">Reuniones interminables cada mañana</p>
              <p className="text-xs sm:text-sm text-gray-600">1 hora o más intentando cuadrar quién entra, quién sale y dónde</p>
            </div>
          </div>

          <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-red-50 rounded-lg sm:rounded-xl border-l-4 border-red-500">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-close-line text-white text-sm sm:text-base"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">Estrés y frustración del equipo</p>
              <p className="text-xs sm:text-sm text-gray-600">Menos tiempo para tareas clínicas, más tiempo en coordinación manual</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
